import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollText, Search, Activity, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  login: 'bg-blue-500/20 text-blue-500',
  logout: 'bg-slate-500/20 text-slate-400',
  create: 'bg-emerald-500/20 text-emerald-500',
  update: 'bg-amber-500/20 text-amber-500',
  delete: 'bg-red-500/20 text-red-500',
  approve: 'bg-emerald-500/20 text-emerald-500',
  reject: 'bg-red-500/20 text-red-500',
  payment: 'bg-emerald-500/20 text-emerald-500',
};

export default function AuditLogViewer() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchLogs(), 300);
    return () => clearTimeout(t);
  }, [q, entityFilter, actionFilter]);

  const fetchAll = async () => {
    try {
      const [s] = await Promise.all([
        axios.get(`${API_URL}/api/audit-logs/summary`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      await fetchLogs();
    } catch { toast.error('Failed to load audit summary'); }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append('q', q);
      if (entityFilter && entityFilter !== 'all') params.append('entity_type', entityFilter);
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      const { data } = await axios.get(`${API_URL}/api/audit-logs?${params.toString()}`, { withCredentials: true });
      setLogs(data.logs || []);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="audit-log-viewer">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2" data-testid="audit-title">Audit Log</h1>
        <p className="text-slate-400">Every login, edit, delete and approval — searchable</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Total Events</span>
              <Activity className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold text-white">{summary.total}</div></CardContent>
          </Card>
          {Object.entries(summary.by_action || {}).slice(0, 3).map(([k, v]) => (
            <Card key={k} className="bg-slate-900 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <span className="text-xs text-slate-400 uppercase tracking-wider capitalize">{k}</span>
                <span className={`px-2 py-1 rounded-sm text-xs ${ACTION_COLORS[k] || 'bg-slate-700 text-slate-300'}`}>{v}</span>
              </CardHeader>
              <CardContent><div className="text-2xl font-bold text-white">{v}</div></CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2 flex items-center space-x-2 bg-slate-800 border border-slate-700 rounded-sm px-3">
              <Search className="h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search description or username..."
                data-testid="audit-search-input"
                className="bg-transparent border-0 focus-visible:ring-0 text-white" />
            </div>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="entity-filter"><SelectValue placeholder="All entities" /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="auth">Auth</SelectItem>
                <SelectItem value="user">Users</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="inventory">Inventory</SelectItem>
                <SelectItem value="installment">Installments</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
                <SelectItem value="lock_unlock">Lock/Unlock</SelectItem>
                <SelectItem value="settings">Settings</SelectItem>
                <SelectItem value="branch">Branches</SelectItem>
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-white">
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login">Login</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400" data-testid="no-logs">No logs match your filters</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Timestamp</TableHead>
                  <TableHead className="text-slate-400">User</TableHead>
                  <TableHead className="text-slate-400">Action</TableHead>
                  <TableHead className="text-slate-400">Entity</TableHead>
                  <TableHead className="text-slate-400">Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="audit-row">
                    <TableCell className="text-slate-300 text-xs whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </TableCell>
                    <TableCell className="text-white">
                      <div className="font-medium">{log.username}</div>
                      <div className="text-xs text-slate-500">{log.role}</div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-sm text-xs capitalize ${ACTION_COLORS[log.action] || 'bg-slate-700 text-slate-300'}`}>
                        {log.action}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-300 capitalize">{log.entity_type}</TableCell>
                    <TableCell className="text-slate-300 max-w-md truncate" title={log.description}>{log.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
